<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-12">
          <h6 class="h2 text-white d-inline-block mb-0">Crear Remito</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row justify-content-center">
        <pre-select-delivery-note v-show="!canCreate" :subtitle="subtitle" @selected="onSelect" />

        <div v-show="canCreate" class="col-12 card-wrapper">
          <div class="card mb-4">
            <div class="card-header">
              <div class="card-body">
                <div class="row">
                  <div class="col-lg-12">
                    <button class="btn base-button btn-neutral btn-sm float-right mt-1" @click="selected = false">
                      Modificar Datos
                    </button>
                  </div>
                </div>

                <delivery-note-info :delivery-note="deliveryNote" />
              </div>

              <hr />

              <div class="card-body">
                <delivery-note-item-form
                  v-if="canCreate"
                  :campaign="deliveryNote.campaign"
                  :distributor-from="deliveryNote.distributorFrom"
                  :distributor-to="deliveryNote.distributorTo"
                  :delivery-note-type="deliveryNote.type"
                  @addItem="addItem"
                />
              </div>

              <div class="card-body">
                <delivery-note-item-list v-if="hasItems" :items="deliveryNote.items" @deleteItem="deleteItem" />
              </div>

              <div class="class-footer">
                <div class="text-right">
                  <base-button :disabled="!isValid" type="primary" @click="onSave">Guardar</base-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import DeliveryNoteItemForm from './Forms/DeliveryNoteItemForm.vue';
import PreSelectDeliveryNote from './Forms/PreSelectDeliveryNote.vue';
import DeliveryNoteInfo from './Show/DeliveryNoteInfo.vue';
import DeliveryNoteItemList from './List/DeliveryNoteItemList.vue';
import { DEVOLUTION, LOAD_DISTRIBUTOR, ADJUST } from '@/util/constants/deliveryNotes';
import swal from 'sweetalert2';
import moment from 'moment';

export default {
  name: 'DeliveryNoteCreate',
  components: {
    RouteBreadCrumb,
    DeliveryNoteItemForm,
    PreSelectDeliveryNote,
    DeliveryNoteInfo,
    DeliveryNoteItemList,
  },

  data() {
    return {
      subtitle:
        'Elija la campaña, distribuidor de salida, entrada, ingrese codigo de remito y pulse en SELECCIONAR para crear el remito',

      selected: false,

      deliveryNote: {
        date: moment().format('YYYY-MM-DD'),
        compliance: false,
        comment: null,
        code: null,
        type: null,
        user: null,
        items: [],

        campaign: null,
        distributorTo: null,
        distributorFrom: null,
        producer: null,
      },
    };
  },

  computed: {
    canCreate() {
      return Boolean(
        this.deliveryNote.campaign &&
          (this.deliveryNote.distributorTo || this.deliveryNote.type === DEVOLUTION) &&
          (this.deliveryNote.distributorFrom ||
            this.deliveryNote.type === LOAD_DISTRIBUTOR ||
            this.deliveryNote.type === ADJUST) &&
          this.deliveryNote.type &&
          this.deliveryNote.user &&
          this.deliveryNote.date &&
          (this.deliveryNote.comment || this.deliveryNote.type !== ADJUST) &&
          this.selected
      );
    },

    hasItems() {
      return this.deliveryNote.items.length > 0;
    },

    isValid() {
      return this.hasItems && this.canCreate;
    },
  },

  methods: {
    addItem(item) {
      this.deliveryNote.items.push(item);
    },

    deleteItem(_, index) {
      this.deliveryNote.items.splice(index, 1);
    },

    async createDeliveryNote() {
      return await this.$services.resource('delivery_notes').create(this.deliveryNote);
    },

    createItemsRequests() {
      const createRequests = [];

      this.deliveryNote.items.forEach((item) => {
        const request = this.$services
          .resource('delivery_note_items')
          .setDeliveryNote(this.deliveryNote.id)
          .create(item);
        createRequests.push(request);
      });

      return Promise.all(createRequests);
    },

    async createItems() {
      try {
        await this.createItemsRequests();

        swal({
          title: 'Listo!',
          text: 'El remito fue creado con éxito.',
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-success',
          type: 'success',
        }).then(() => this.$router.push({ name: 'DeliveryNotesList' }));
      } catch (err) {
        swal({
          title: 'Error!',
          text: err.message,
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-primary',
          type: 'error',
        });
      }
    },

    async onSave() {
      try {
        const { data } = await this.createDeliveryNote();

        this.deliveryNote.id = data.id;

        this.createItems();
      } catch (err) {
        swal({
          title: 'Error!',
          text: err.message,
          buttonsStyling: false,
          confirmButtonClass: 'btn btn-primary',
          type: 'error',
        });
      }
    },

    async onSelect(formData) {
      const [campaign, distributorTo, distributorFrom, producer] = await Promise.all([
        this.fetchCampaign(formData.campaign),
        this.fetchDistributor(formData.distributorTo),
        this.fetchDistributor(formData.distributorFrom),
        this.fetchProducer(formData.producer),
      ]);

      this.deliveryNote.campaign = campaign;
      this.deliveryNote.producer = producer;
      this.deliveryNote.distributorTo = distributorTo;
      this.deliveryNote.distributorFrom = distributorFrom;

      this.deliveryNote.code = formData.code;
      this.deliveryNote.type = formData.type;
      this.deliveryNote.user = formData.user;
      this.deliveryNote.comment = formData.comment;

      this.selected = true;
    },

    async fetchCampaign(id) {
      return await this.$services
        .resource('campaigns')
        .show(id, { include: ['crops', 'articles'] })
        .then((res) => res.data.data);
    },

    async fetchDistributor(id) {
      if (!id) {
        return null;
      }

      return await this.$services
        .resource('distributors')
        .show(id, { include: ['seller'] })
        .then((res) => res.data.data);
    },

    async fetchProducer(id) {
      if (!id) {
        return null;
      }

      return await this.$services
        .resource('producers')
        .show(id)
        .then((res) => res.data.data);
    },
  },
};
</script>
